// «Мои документы»: загруженные файлы, собранные в чате документы и расчёты — одним списком, по папкам.
// Таблицей (название, тип, размер, дата) или плиткой с превью, с поиском, сортировкой и избранным.
// Флажками выбирают несколько документов: скачать архивом, переместить, удалить. Файлы загружают кнопкой
// или перетаскивают на страницу, документы перетаскивают на папку (или «Переместить в…» в меню «⋯»).
import { useEffect, useRef, useState, type DragEvent, type MouseEvent, type ReactNode } from "react";
import { getJson } from "../api";
import { boot, type Paragraph } from "../boot";
import { ActionMenu, FolderNameField, type MenuAction } from "../components/ActionMenu";
import { DocPage } from "../components/DocPage";
import { Icon } from "../components/Icon";
import { openViewer } from "../components/Viewer";
import { pickFiles } from "../lib/files";
import { choiceLabel, nb, plural } from "../lib/text";
import { navigate } from "../router";
import {
  createFolder, deleteMany, DOC_DRAG, docName, downloadMany, foldersStore, foundText, itemMeta, moveMany, moveToFolder, openCalc,
  openLogin, removeFolder, renameEntry, renameFolder, savedStore, starMany, uploadedStore, uploadFile, useStore, type Folder,
  type FolderEntry, type FoundValue, type UploadedDoc,
} from "../store";

// Найденные данные с цитатами: что, откуда взято и что стоит проверить.
export function FoundList({ fields }: { fields: Record<string, FoundValue> }) {
  return (
    <ul className="found-list">
      {Object.entries(fields).map(([key, item]) => (
        <li key={key}>
          <span className="found-label">{item.label || key}</span>
          <span className="found-value">
            {choiceLabel(item.value)}
            <span className="found-quote">«{item.quote}»</span>
            {item.note && <span className="found-note">{item.note}</span>}
          </span>
        </li>
      ))}
    </ul>
  );
}

type Item = FolderEntry & { key: string; date: string; title: string; meta: string; type: string; size: string; icon: string };
type Sort = "new" | "old" | "name" | "starred";
type View = "grid" | "list";

const SORTS: { value: Sort; label: string }[] = [
  { value: "new", label: "Сначала новые" }, { value: "old", label: "Сначала старые" },
  { value: "name", label: "По названию" }, { value: "starred", label: "Сначала избранные" },
];
const IMAGES = new Set([".jpg", ".jpeg", ".png"]);

function remembered<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const value = localStorage.getItem(key) as T | null;
    return value && allowed.includes(value) ? value : fallback;
  } catch { return fallback; }
}
function remember(key: string, value: string) {
  try { localStorage.setItem(key, value); } catch { /* необязательная настройка */ }
}

const fullDate = (iso: string) => new Date(iso).toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric" });

function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} МБ`;
}

// Главное действие по щелчку: документ открывается справа (просмотр), расчёт — в чате.
function openItem(item: Item) {
  if (item.kind === "upload") return openViewer({ kind: "upload", id: item.item.id });
  if (item.item.kind === "calc") return openCalc(item.item);
  openViewer({ kind: "saved", id: item.item.id });
}
const openLabel = (item: Item) => (item.kind === "saved" && item.item.kind === "calc" ? "Открыть расчёт" : "Открыть справа");

// Данные, найденные в загруженном документе, — в отдельном окне.
function FoundDialog({ doc, onClose }: { doc: UploadedDoc | null; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const [fields, setFields] = useState<Record<string, FoundValue> | null>(null);
  useEffect(() => {
    if (!doc) return;
    let current = true;
    setFields(null);
    if (ref.current && !ref.current.open) ref.current.showModal();
    getJson<UploadedDoc>(`/api/documents/${doc.id}`).then((full) => { if (current) setFields(full.fields || {}); })
      .catch((error) => { if (current) { alert((error as Error).message); onClose(); } });
    return () => { current = false; };
  }, [doc]);
  return (
    <dialog ref={ref} className="picker-dialog" aria-labelledby="found-title" onClose={onClose}
      onClick={(event) => { if (event.target === event.currentTarget) event.currentTarget.close(); }}>
      <div className="picker-head">
        <h2 id="found-title">Данные из документа{doc ? ` «${docName(doc)}»` : ""}</h2>
        <form method="dialog"><button className="icon-button" aria-label="Закрыть"><Icon name="close" /></button></form>
      </div>
      <div className="picker-body">{fields ? <FoundList fields={fields} /> : <p className="picker-empty">Открываю…</p>}</div>
    </dialog>
  );
}

// Превью собранного документа с ответами человека: загружается, когда плитка показалась на экране, и запоминается.
const previews = new Map<string, Promise<Paragraph[] | null>>();
function useSavedPreview(id: string, enabled: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  const [paragraphs, setParagraphs] = useState<Paragraph[] | null>(null);
  useEffect(() => {
    if (!enabled || !ref.current) return;
    let current = true;
    const load = () => {
      if (!previews.has(id)) previews.set(id, getJson<Paragraph[]>(`/api/saved/${id}/preview`).catch(() => null));
      previews.get(id)!.then((found) => { if (current) setParagraphs(found); });
    };
    const observer = new IntersectionObserver((seen) => { if (seen.some((one) => one.isIntersecting)) { observer.disconnect(); load(); } });
    observer.observe(ref.current);
    return () => { current = false; observer.disconnect(); };
  }, [id, enabled]);
  return { ref, paragraphs };
}

// Что видно на плитке: фото — само фото, файл — начало текста, собранный документ — его лист, расчёт — сумма.
function Thumb({ item }: { item: Item }) {
  const savedDoc = item.kind === "saved" && item.item.kind === "doc";
  const { ref, paragraphs } = useSavedPreview(item.item.id, savedDoc);
  let body: ReactNode;
  if (item.kind === "upload" && IMAGES.has(item.item.ext || "")) {
    body = <img src={`/api/documents/${item.item.id}/file`} alt="" loading="lazy" />;
  } else if (item.kind === "upload") {
    body = <span className="tile-page"><span className="tile-text">{item.item.preview || item.title}</span></span>;
  } else if (item.item.kind === "calc") {
    body = <span className="tile-calc"><span>Госпошлина</span><strong>{nb(String(item.item.payload.to_pay || item.item.meta || ""))}</strong></span>;
  } else {
    body = <span className="tile-page"><span className="doc-thumb">{paragraphs ? <DocPage paragraphs={paragraphs} /> : <span className="tile-text">{item.title}</span>}</span></span>;
  }
  return <div ref={ref} className="tile-thumb" aria-hidden="true">{body}</div>;
}

// Флажок выбора: щелчок передаётся целиком — с Shift выбирается диапазон.
function Check({ checked, mixed = false, label, onChange }: { checked: boolean; mixed?: boolean; label: string; onChange: (event: MouseEvent) => void }) {
  return (
    <input type="checkbox" className="docs-check" checked={checked} aria-label={label} onChange={() => { /* состояние меняет onClick */ }}
      ref={(node) => { if (node) node.indeterminate = mixed; }} onClick={(event) => { event.stopPropagation(); onChange(event); }} />
  );
}

type Upload = { name: string; index: number; total: number; share: number; reading: boolean };

export function MyPage() {
  const saved = useStore(savedStore);
  const uploaded = useStore(uploadedStore);
  const folders = useStore(foldersStore);
  const [query, setQuery] = useState("");
  const [view, setView] = useState<View>(() => remembered("pravosvod.myView", ["grid", "list"] as const, "list"));
  const [sort, setSort] = useState<Sort>(() => remembered("pravosvod.mySort", ["new", "old", "name", "starred"] as const, "new"));
  const [folderId, setFolderId] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);      // "new", id папки или ключ документа (s…, u…)
  const [upload, setUpload] = useState<Upload | null>(null);
  const [status, setStatus] = useState<{ text: ReactNode; error: boolean } | null>(null);
  const [dragged, setDragged] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);             // id папки, "root" или "files"
  const [details, setDetails] = useState<UploadedDoc | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const anchor = useRef<string | null>(null);                        // откуда тянуть выбор с Shift

  const folder = folders.find((one) => one.id === folderId) || null;
  useEffect(() => { if (folderId && !folder) setFolderId(null); }, [folderId, folder]);   // папку удалили
  useEffect(() => { setSelected(new Set()); }, [folderId]);

  const known = new Set(folders.map((one) => one.id));
  const folderName = new Map(folders.map((one) => [one.id, one.name]));
  const items: Item[] = [
    ...uploaded.map((doc): Item => ({ kind: "upload", item: doc, key: `u${doc.id}`, date: doc.created_at, title: docName(doc),
      meta: doc.kind === "unknown" ? "Загружен" : foundText(doc.found), icon: "upload",
      type: (doc.ext || doc.filename.slice(doc.filename.lastIndexOf("."))).replace(".", "").toUpperCase() || "Файл", size: fileSize(doc.size) })),
    ...[...saved.doc, ...saved.calc].map((item): Item => ({ kind: "saved", item, key: `s${item.id}`, date: item.created_at, title: item.title,
      meta: itemMeta(item), icon: item.kind === "doc" ? "doc" : "calc", type: item.kind === "doc" ? "Word" : "Расчёт", size: "—" })),
  ];
  const placeOf = (item: Item) => (item.item.folder_id && known.has(item.item.folder_id) ? item.item.folder_id : null);
  const text = query.trim().toLocaleLowerCase("ru");
  const matches = (item: Item) => `${item.title} ${item.meta} ${item.kind === "upload" ? item.item.filename : ""}`.toLocaleLowerCase("ru").includes(text);
  const byDate = (a: Item, b: Item) => b.date.localeCompare(a.date);
  const order = (a: Item, b: Item) => (sort === "name" ? a.title.localeCompare(b.title, "ru") : sort === "old" ? a.date.localeCompare(b.date)
    : sort === "starred" ? Number(Boolean(b.item.starred)) - Number(Boolean(a.item.starred)) || byDate(a, b) : byDate(a, b));
  // Поиск ищет по всем папкам сразу; без поиска — содержимое открытой папки или то, что не разложено.
  const shown = items.filter((item) => (text ? matches(item) : placeOf(item) === folderId)).sort(order);
  const shownFolders = folderId ? [] : folders.filter((one) => !text || one.name.toLocaleLowerCase("ru").includes(text));
  const countIn = (id: string) => items.filter((item) => placeOf(item) === id).length;
  const empty = !items.length && !folders.length;
  const chosen = shown.filter((item) => selected.has(item.key));
  const allChosen = shown.length > 0 && chosen.length === shown.length;

  // Флажок: обычный щелчок — один документ, с Shift — все между ним и прошлым выбранным.
  function toggle(item: Item, event: MouseEvent) {
    const next = new Set(selected);
    const keys = shown.map((one) => one.key);
    if (event.shiftKey && anchor.current && keys.includes(anchor.current)) {
      const [from, to] = [keys.indexOf(anchor.current), keys.indexOf(item.key)].sort((a, b) => a - b);
      for (const key of keys.slice(from, to + 1)) next.add(key);
    } else if (next.has(item.key)) next.delete(item.key);
    else next.add(item.key);
    anchor.current = item.key;
    setSelected(next);
  }
  const clearSelection = () => setSelected(new Set());

  async function uploadFiles(files: File[]) {
    if (!files.length || upload) return;
    setStatus(null);
    let last: UploadedDoc | null = null;
    const errors: string[] = [];
    for (const [index, file] of files.entries()) {
      setUpload({ name: file.name, index, total: files.length, share: 0, reading: false });
      try {
        const doc = await uploadFile(file, (share) => setUpload((now) => now && { ...now, share, reading: share >= 1 }));
        if (folderId) await moveToFolder({ kind: "upload", item: doc }, folderId);   // загрузили в открытую папку — туда и кладём
        last = doc;
      } catch (error) {
        errors.push(`«${file.name}»: ${(error as Error).message}`);
      }
    }
    setUpload(null);
    if (errors.length) setStatus({ text: errors.join(" "), error: true });
    else if (last && files.length === 1) {
      setStatus({ text: <>
        {last.kind === "unknown" ? `«${last.filename}» сохранён. Можно перейти к разбору.` : `Готово: ${last.title} — ${foundText(last.found)}.`}
        {" "}<a href={`/?start=review:${last.id}`}>Разобрать документ →</a>
      </>, error: false });
    } else if (last) setStatus({ text: `Загружено документов: ${files.length}.`, error: false });
  }
  const pick = async () => uploadFiles(await pickFiles());

  // Файлы с компьютера — на всю страницу; документ из списка — на папку или на «Мои документы» (убрать из папки).
  const pageDrop = {
    onDragOver: (event: DragEvent) => {
      if (!event.dataTransfer.types.includes("Files")) return;
      event.preventDefault();
      if (over !== "files") setOver("files");
    },
    onDragLeave: (event: DragEvent) => { if (!event.currentTarget.contains(event.relatedTarget as Node)) setOver(null); },
    onDrop: (event: DragEvent) => {
      if (!event.dataTransfer.types.includes("Files")) return;
      event.preventDefault();
      setOver(null);
      uploadFiles([...event.dataTransfer.files]);
    },
  };
  const folderDrop = (target: string, id: string | null) => ({
    onDragOver: (event: DragEvent) => {
      if (!event.dataTransfer.types.includes(DOC_DRAG)) return;
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = "move";
      if (over !== target) setOver(target);
    },
    onDragLeave: (event: DragEvent) => { if (!event.currentTarget.contains(event.relatedTarget as Node)) setOver(null); },
    onDrop: (event: DragEvent) => {
      if (!event.dataTransfer.types.includes(DOC_DRAG)) return;
      event.preventDefault();
      event.stopPropagation();
      setOver(null);
      setDragged(null);
      const item = items.find((one) => one.key === event.dataTransfer.getData(DOC_DRAG));
      if (!item) return;
      // Тянут один из выбранных — переезжают все выбранные.
      if (selected.has(item.key) && chosen.length > 1) moveMany(chosen, id);
      else moveToFolder(item, id);
    },
  });
  const dragProps = (item: Item) => ({
    draggable: editing !== item.key,
    onDragStart: (event: DragEvent) => { event.dataTransfer.setData(DOC_DRAG, item.key); event.dataTransfer.effectAllowed = "move"; setDragged(item.key); },
    onDragEnd: () => { setDragged(null); setOver(null); },
  });

  function moveTargets(entries: Item[], place: string | null): MenuAction[] {
    return [
      ...folders.filter((one) => one.id !== place).map((one) => ({ label: one.name, icon: "folder", run: () => moveMany(entries, one.id) })),
      ...(place || entries.some((entry) => placeOf(entry)) ? [{ label: "Без папки", icon: "arrow", run: () => moveMany(entries, null) }] : []),
    ];
  }

  function itemActions(item: Item): MenuAction[] {
    const place = placeOf(item);
    const actions: MenuAction[] = [
      { label: "Открыть", icon: "external", run: () => openItem(item) },
      { label: "Скачать", icon: "download", run: () => downloadMany([item]) },
      { label: item.item.starred ? "Убрать из избранного" : "В избранное", icon: "star", run: () => starMany([item], !item.item.starred) },
    ];
    if (item.kind === "upload") {
      const doc = item.item;
      actions.push({ label: "Разобрать документ", icon: "search", run: () => navigate(`/?start=review:${doc.id}`) });
      if (doc.found) actions.push({ label: "Данные из документа", icon: "lines", run: () => setDetails(doc) });
      if (doc.template) actions.push({ label: `Составить: ${doc.template_title}`, icon: "edit", run: () => navigate(`/?start=doc:${doc.template}&from=${doc.id}`) });
    }
    actions.push({ label: "Переименовать", icon: "edit", run: () => setEditing(item.key) });
    const targets = moveTargets([item], place);
    if (targets.length) actions.push({ label: "Переместить в…", icon: "moveTo", items: targets });
    actions.push({ label: "Удалить", icon: "trash", danger: true, divider: true, run: () => deleteMany([item]) });
    return actions;
  }

  function folderActions(one: Folder): MenuAction[] {
    return [
      { label: "Открыть", icon: "external", run: () => openFolder(one) },
      { label: "Переименовать", icon: "edit", run: () => setEditing(one.id) },
      { label: "Удалить папку", icon: "trash", danger: true, divider: true, run: () => removeFolder(one) },
    ];
  }
  const openFolder = (one: Folder) => { setFolderId(one.id); setQuery(""); };

  const star = (item: Item) => (
    <button type="button" className={`docs-star${item.item.starred ? " is-on" : ""}`} aria-pressed={Boolean(item.item.starred)}
      aria-label={item.item.starred ? `Убрать «${item.title}» из избранного` : `Добавить «${item.title}» в избранное`}
      title={item.item.starred ? "Убрать из избранного" : "В избранное"} onClick={() => starMany([item], !item.item.starred)}>
      <Icon name="star" />
    </button>
  );

  const nameCell = (item: Item) => editing === item.key ? (
    <FolderNameField initial={item.title} label="Новое название документа" placeholder="Название документа" maxLength={120}
      onSave={(name) => renameEntry(item, name)} onDone={() => setEditing(null)} />
  ) : (
    <button type="button" className="docs-open" onClick={() => openItem(item)} title={openLabel(item)}>
      <span className={`docs-icon is-${item.icon}`}><Icon name={item.icon} /></span>
      <span className="docs-text">
        <span className="docs-title">{item.title}</span>
        <span className="docs-meta">{[item.meta, text && placeOf(item) ? `папка «${folderName.get(placeOf(item)!)}»` : ""].filter(Boolean).join(" · ")}</span>
      </span>
    </button>
  );

  // ---------------------------------------------------------------- таблица

  function folderRow(one: Folder) {
    const count = countIn(one.id);
    return (
      <tr key={one.id} className={`docs-entry is-folder${over === one.id ? " is-over" : ""}`} {...folderDrop(one.id, one.id)}>
        <td className="docs-cell-check" />
        <td className="docs-cell-name">
          {editing === one.id ? (
            <FolderNameField initial={one.name} label="Новое название папки" onSave={(name) => renameFolder(one, name)} onDone={() => setEditing(null)} />
          ) : (
            <button type="button" className="docs-open" onClick={() => openFolder(one)} aria-label={`Папка «${one.name}»`}>
              <span className="docs-icon is-folder"><Icon name="folder" /></span>
              <span className="docs-text"><span className="docs-title">{one.name}</span></span>
            </button>
          )}
        </td>
        <td className="docs-cell-type">Папка</td>
        <td className="docs-cell-size">{count ? plural(count, "документ", "документа", "документов") : "Пусто"}</td>
        <td className="docs-cell-date">{fullDate(one.created_at)}</td>
        <td className="docs-cell-star" />
        <td className="docs-cell-menu"><ActionMenu label={`Действия с папкой «${one.name}»`} actions={folderActions(one)} /></td>
      </tr>
    );
  }

  function itemRow(item: Item) {
    const isChosen = selected.has(item.key);
    return (
      <tr key={item.key} id={item.kind === "upload" ? `upload-${item.item.id}` : undefined} {...dragProps(item)}
        className={`docs-entry${isChosen ? " is-chosen" : ""}${dragged === item.key ? " is-dragging" : ""}`}>
        <td className="docs-cell-check"><Check checked={isChosen} label={`Выбрать «${item.title}»`} onChange={(event) => toggle(item, event)} /></td>
        <td className="docs-cell-name">{nameCell(item)}</td>
        <td className="docs-cell-type">{item.type}</td>
        <td className="docs-cell-size">{item.size}</td>
        <td className="docs-cell-date">{fullDate(item.date)}</td>
        <td className="docs-cell-star">{star(item)}</td>
        <td className="docs-cell-menu"><ActionMenu label={`Действия с «${item.title}»`} actions={itemActions(item)} /></td>
      </tr>
    );
  }

  // ---------------------------------------------------------------- плитка

  function folderTile(one: Folder) {
    const count = countIn(one.id);
    return (
      <li key={one.id} className={`docs-entry docs-tile is-folder${over === one.id ? " is-over" : ""}`} {...folderDrop(one.id, one.id)}>
        <button type="button" className="tile-open" onClick={() => openFolder(one)} aria-label={`Папка «${one.name}»`}>
          <span className="tile-thumb is-folder" aria-hidden="true"><Icon name="folder" /></span>
        </button>
        <div className="tile-tools"><ActionMenu label={`Действия с папкой «${one.name}»`} actions={folderActions(one)} /></div>
        <div className="tile-caption">
          {editing === one.id ? (
            <FolderNameField initial={one.name} label="Новое название папки" onSave={(name) => renameFolder(one, name)} onDone={() => setEditing(null)} />
          ) : (
            <>
              <span className="docs-title">{one.name}</span>
              <span className="docs-meta">{count ? plural(count, "документ", "документа", "документов") : "Пусто"}</span>
            </>
          )}
        </div>
      </li>
    );
  }

  function itemTile(item: Item) {
    const isChosen = selected.has(item.key);
    return (
      <li key={item.key} id={item.kind === "upload" ? `upload-${item.item.id}` : undefined} {...dragProps(item)}
        className={`docs-entry docs-tile${isChosen ? " is-chosen" : ""}${dragged === item.key ? " is-dragging" : ""}`}>
        <button type="button" className="tile-open" onClick={() => openItem(item)} title={openLabel(item)} aria-label={`${openLabel(item)}: «${item.title}»`}>
          <Thumb item={item} />
        </button>
        <div className="tile-check"><Check checked={isChosen} label={`Выбрать «${item.title}»`} onChange={(event) => toggle(item, event)} /></div>
        <div className="tile-tools">
          {item.item.starred && star(item)}
          <ActionMenu label={`Действия с «${item.title}»`} actions={itemActions(item)} />
        </div>
        <div className="tile-caption">
          {editing === item.key ? nameCell(item) : (
            <>
              <span className="docs-title" title={item.title}>{item.title}</span>
              <span className="docs-meta">{[item.type, item.size !== "—" ? item.size : "", fullDate(item.date),
                text && placeOf(item) ? `папка «${folderName.get(placeOf(item)!)}»` : ""].filter(Boolean).join(" · ")}</span>
            </>
          )}
        </div>
      </li>
    );
  }

  return (
    <div className={`docs-page${over === "files" ? " is-dropping" : ""}`} {...pageDrop}>
      <h1>Мои документы</h1>
      {boot.isGuest && (
        <div className="guest-banner">
          <p>Вы работаете как гость: документы и расчёты привязаны к этому браузеру. Войдите, чтобы они хранились в аккаунте и открывались с любого устройства.</p>
          <button type="button" className="button" onClick={() => openLogin()}>Войти</button>
        </div>
      )}

      <div className="docs-toolbar">
        <button type="button" className="docs-button is-primary" onClick={pick} disabled={!!upload}><Icon name="upload" />Загрузить</button>
        {!folderId && <button type="button" className="docs-button" onClick={() => setEditing("new")}><Icon name="folderPlus" />Новая папка</button>}
        <ActionMenu label={`Сортировка: ${SORTS.find((one) => one.value === sort)!.label.toLowerCase()}`} className="docs-button is-icon"
          actions={SORTS.map((one) => ({ label: one.label, icon: one.value === sort ? "check" : "sort", checked: one.value === sort,
            run: () => { setSort(one.value); remember("pravosvod.mySort", one.value); } }))}>
          <Icon name="sort" />
        </ActionMenu>
        <span className="docs-spacer" />
        <label className="docs-search">
          <Icon name="search" />
          <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Поиск по документам" aria-label="Поиск по документам" />
        </label>
        <div className="docs-view" role="group" aria-label="Вид списка">
          {([["grid", "grid", "Плиткой"], ["list", "lines", "Списком"]] as const).map(([value, icon, label]) => (
            <button key={value} type="button" aria-label={label} title={label} aria-pressed={view === value}
              onClick={() => { setView(value); remember("pravosvod.myView", value); }}><Icon name={icon} /></button>
          ))}
        </div>
      </div>

      {chosen.length > 0 && (
        <div className="docs-bulk" role="region" aria-label="Выбранные документы">
          <span className="docs-bulk-count">Выбрано: {chosen.length}</span>
          <span className="docs-spacer" />
          <button type="button" className="docs-button" onClick={() => downloadMany(chosen)}>Скачать</button>
          {moveTargets(chosen, folderId).length > 0 && (
            <ActionMenu label="Переместить" title="Переместить выбранные" className="docs-button" actions={moveTargets(chosen, folderId)}>Переместить</ActionMenu>
          )}
          <button type="button" className="docs-button" onClick={async () => { if (await deleteMany(chosen)) clearSelection(); }}>Удалить</button>
          <button type="button" className="docs-button is-quiet" onClick={clearSelection}>Снять выделение</button>
        </div>
      )}

      {upload && (
        <div className="docs-progress" role="status">
          <span className="docs-progress-bar"><span style={{ width: `${Math.round((upload.reading ? 1 : upload.share) * 100)}%` }} className={upload.reading ? "is-reading" : undefined} /></span>
          <span className="docs-progress-text">
            {upload.reading ? "Читаю документ…" : "Загружаем…"}{upload.total > 1 ? ` ${upload.index + 1} из ${upload.total}` : ""}
          </span>
        </div>
      )}
      {status && <p className={`upload-status${status.error ? " is-error" : ""}`} role="status">{status.text}</p>}

      {folder && (
        <nav className="docs-crumbs" aria-label="Папки">
          <button type="button" className={over === "root" ? "is-over" : undefined} onClick={() => setFolderId(null)} {...folderDrop("root", null)}>
            <Icon name="arrow" />Мои документы
          </button>
          <span aria-hidden="true">/</span>
          <strong>{folder.name}</strong>
        </nav>
      )}

      {editing === "new" && (
        <div className="docs-new-folder">
          <span className="docs-icon is-folder"><Icon name="folder" /></span>
          <FolderNameField initial="" label="Название новой папки" onSave={(name) => { createFolder(name); }} onDone={() => setEditing(null)} />
        </div>
      )}

      {empty ? (
        <section className="docs-empty">
          <span className="docs-empty-icon"><Icon name="folderOpen" /></span>
          <h2>Здесь будут все ваши документы</h2>
          <p>Загружайте файлы или создавайте их в чате — всё, что вы прислали или собрали, окажется здесь.</p>
          <button type="button" className="docs-button is-primary" onClick={pick} disabled={!!upload}><Icon name="upload" />Загрузить</button>
        </section>
      ) : (
        <>
          {view === "list" ? (
            <table className="docs-table" aria-label={folder ? `Папка «${folder.name}»` : "Документы"}>
              <thead>
                <tr>
                  <th className="docs-cell-check">
                    {shown.length > 0 && <Check checked={allChosen} mixed={chosen.length > 0 && !allChosen} label="Выбрать все"
                      onChange={() => setSelected(allChosen ? new Set() : new Set(shown.map((item) => item.key)))} />}
                  </th>
                  <th className="docs-cell-name">Название</th>
                  <th className="docs-cell-type">Тип</th>
                  <th className="docs-cell-size">Размер</th>
                  <th className="docs-cell-date">Изменён</th>
                  <th className="docs-cell-star"><span className="visually-hidden">Избранное</span></th>
                  <th className="docs-cell-menu"><span className="visually-hidden">Действия</span></th>
                </tr>
              </thead>
              <tbody>
                {shownFolders.map(folderRow)}
                {shown.map(itemRow)}
              </tbody>
            </table>
          ) : (
            <ul className={`docs-grid${chosen.length ? " has-chosen" : ""}`} aria-label={folder ? `Папка «${folder.name}»` : "Документы"}>
              {shownFolders.map(folderTile)}
              {shown.map(itemTile)}
            </ul>
          )}
          {!shown.length && !shownFolders.length && (
            <p className="docs-nothing">{text ? "Ничего не найдено. Попробуйте другое название." : folder
              ? "Папка пуста. Перетащите сюда документы или выберите «Переместить в…» в меню «⋯» документа." : "Все документы разложены по папкам."}</p>
          )}
        </>
      )}

      <FoundDialog doc={details} onClose={() => setDetails(null)} />
    </div>
  );
}
